// src/hooks/useLocation.js
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

export default function useLocation() {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // axios instance
  const client = useMemo(() => {
    return axios.create({
      baseURL: "http://172.203.236.174:9000",
      timeout: 15000,
    });
  }, []);

  // fetch countries (GET /location/countries)
  useEffect(() => {
    let controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await client.get("/location/countries", {
          signal: controller.signal,
        });
        const data = resp?.data ?? [];
        // normalize countries to [{ name }]
        const mapped = Array.isArray(data)
          ? data.map((c) =>
              typeof c === "string" ? { name: c } : { name: c?.name ?? "" }
            )
          : [];
        setCountries(mapped.filter((c) => c.name));
      } catch (err) {
        if (!axios.isCancel(err) && err.name !== "CanceledError") {
          console.error("useLocation - countries error:", err);
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [client]);

  // fetch states when country changes (GET /location/states/{country})
  useEffect(() => {
    if (!selectedCountry) {
      setStates([]);
      setSelectedState("");
      setCities([]);
      setSelectedCity("");
      return;
    }

    let controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const urlCountry = encodeURIComponent(selectedCountry);
        const resp = await client.get(`/location/states/${urlCountry}`, {
          signal: controller.signal,
        });

        const json = resp?.data;

        // try multiple shapes and reduce to array of state names
        let statesArr = [];
        if (Array.isArray(json)) {
          statesArr = json.map((s) => (s?.name ? s.name : s));
        } else if (json?.data && Array.isArray(json.data)) {
          statesArr = json.data.map((s) => (s?.name ? s.name : s));
        } else if (Array.isArray(json?.states)) {
          statesArr = json.states.map((s) => (s?.name ? s.name : s));
        } else if (Array.isArray(json?.data?.states)) {
          statesArr = json.data.states.map((s) => (s?.name ? s.name : s));
        } else if (typeof json === "object" && json?.states && Array.isArray(json.states)) {
          statesArr = json.states.map((s) => (s?.name ? s.name : s));
        }

        // remove falsy and duplicates
        statesArr = Array.from(new Set(statesArr.filter(Boolean)));

        setStates(statesArr);
        setSelectedState("");
        setCities([]);
        setSelectedCity("");
      } catch (err) {
        if (!axios.isCancel(err) && err.name !== "CanceledError") {
          console.error("useLocation - states error:", err);
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [client, selectedCountry]);

  // fetch cities when state changes (GET /location/cities/{country}/{state})
  useEffect(() => {
    if (!selectedCountry || !selectedState) {
      setCities([]);
      setSelectedCity("");
      return;
    }

    let controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const urlCountry = encodeURIComponent(selectedCountry);
        const urlState = encodeURIComponent(selectedState);
        const resp = await client.get(
          `/location/cities/${urlCountry}/${urlState}`,
          { signal: controller.signal }
        );

        const json = resp?.data;
        let citiesArr = [];
        if (Array.isArray(json)) {
          citiesArr = json;
        } else if (json?.data && Array.isArray(json.data)) {
          citiesArr = json.data;
        } else if (Array.isArray(json?.cities)) {
          citiesArr = json.cities;
        } else if (Array.isArray(json?.data?.cities)) {
          citiesArr = json.data.cities;
        }

        citiesArr = Array.from(new Set(citiesArr.filter(Boolean)));
        setCities(citiesArr);
        setSelectedCity("");
      } catch (err) {
        if (!axios.isCancel(err) && err.name !== "CanceledError") {
          console.error("useLocation - cities error:", err);
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [client, selectedCountry, selectedState]);

  return {
    countries,
    states,
    cities,
    selectedCountry,
    selectedState,
    selectedCity,
    setSelectedCountry,
    setSelectedState,
    setSelectedCity,
    loading,
    error,
  };
}
